/**
 * Location Breadcrumb
 * Automatically generats nav links in dashboard header
 */
import { useLocation } from '@gatsbyjs/reach-router';
import { Box, Breadcrumbs } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';

import { capitalizeAll } from '@utils/format';

import BreadcrumbLink from './BreadcrumbLink';

const messages = defineMessages({
  settingTitle: {
    id: 'organization.setting.title',
    defaultMessage: 'Organization Settings',
  },
  managementTitle: {
    id: 'organization.management.title',
    defaultMessage: 'Organization Management',
  },
});

const LocationBreadcrumb: React.FC<{
  friendlyName?: string | null;
  color?: string;
}> = ({ friendlyName = '', color }) => {
  const { formatMessage } = useIntl();
  const mobile = useIsMobile();
  const location = useLocation();
  const { palette } = useTheme();
  const {
    state: { isVitall },
  } = useSession();

  const resolvedColor = color ?? palette.text.primary;

  const specialCases: Record<string, string | undefined> = {
    labs_cbc: 'Labs CBC',
    'account-management': isVitall
      ? formatMessage(messages.managementTitle)
      : formatMessage(messages.settingTitle),
  };

  // Remove dash
  function cleanName(str: string) {
    // Special cases
    if (specialCases[str]) {
      return specialCases[str];
    }

    // Just clean up
    return capitalizeAll(str.replace(/[-_]/gu, ' '), true);
  }

  // Split up url
  const splitUrl = location.pathname.split('/').filter((name) => name);

  // Set paths
  const locationPaths = splitUrl.map((value, index) => {
    let label = value;
    if (friendlyName && index === splitUrl.length - 1) {
      label = friendlyName;
    }
    return {
      pathname: splitUrl.slice(0, index + 1).join('/'),
      label: cleanName(label),
    };
  });
  const displayedPaths = locationPaths.slice(
    Math.max(mobile ? locationPaths.length - 2 : 0)
  );

  // Code below creates an array of all the paths the user will be able to click on
  return (
    <Breadcrumbs
      data-testid="location-breadcrumb-container"
      separator={<Icon type="fad" name="chevron-right" />}
      sx={{
        height: {
          sm: '100%',
        },
        padding: {
          xs: 2,
          sm: 0,
        },
      }}
    >
      {displayedPaths.map(({ label, pathname }, index) => {
        if (index !== displayedPaths.length - 1) {
          if (mobile) {
            return (
              <BreadcrumbLink
                color={resolvedColor}
                key={`${pathname}-${index}`}
                name={label}
                to={`/${pathname}`}
              />
            );
          }

          return (
            <BreadcrumbLink
              color={resolvedColor}
              key={`${pathname}-${index}`}
              name={label}
              to={`/${pathname}`}
            />
          );
        }

        if (!mobile) {
          return (
            <Box
              key={`${pathname}-${index}`}
              sx={{
                color: resolvedColor,
                fontWeight:
                  index === displayedPaths.length - 1 ? '500' : 'normal',
                fontSize: '20px',
              }}
            >
              {label}
            </Box>
          );
        }

        return null;
      })}
    </Breadcrumbs>
  );
};

export default LocationBreadcrumb;
